/**
 * Overview Tab Mockup - UX/UI Audit Enhancement Preview
 *
 * This is a MOCKUP showing proposed improvements to the Overview tab.
 * It uses sample data and is completely self-contained.
 *
 * PROPOSED CHANGES (marked with "ENHANCED"):
 * 1. ActivityTrendsCard: Added sparkline mini-charts for visual context
 * 2. MTDReviewCard Guidance: Added icons for faster scanning
 * 3. FunnelStoryCard: Improved responsive breakpoints for conversion grid
 *
 * Created: December 3, 2025 for UX/UI Audit
 */

import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, ResponsiveContainer
} from 'recharts';

// ============================================
// SAMPLE DATA - Mimics real data patterns
// ============================================
const SAMPLE_REPS = ['John Smith', 'Jane Doe', 'Mike Johnson', 'Sarah Williams', 'Alex Brown'];
const CURRENT_MONTH = 'December';
const BUSINESS_DAYS_ELAPSED = 3;
const BUSINESS_DAYS_REMAINING = 19;
const BUSINESS_DAYS_IN_MONTH = 22;

const SAMPLE_METRICS = {
  team: {
    revenue: 145000, revenueGoal: 500000,
    apps: 12, appsGoal: 45,
    calls: 342, callsGoal: 1100,
    funded: 4, fundedGoal: 15,
    approved: 8, approvedGoal: 25
  },
  'John Smith': {
    revenue: 45000, revenueGoal: 100000,
    apps: 4, appsGoal: 9,
    calls: 98, callsGoal: 220,
    funded: 2, fundedGoal: 3,
    approved: 3, approvedGoal: 5
  }
};

const SAMPLE_FUNNEL_DATA = {
  calls: 342, sms: 189, apps: 12, approved: 8, funded: 4, revenue: 145000,
  conversions: {
    callsToApps: 3.5, callsToAppsHist: 3.2,
    smsToApps: 6.3, smsToAppsHist: 5.8,
    appsToApproved: 66.7, appsToApprovedHist: 62.0,
    approvedToFunded: 50.0, approvedToFundedHist: 55.0
  }
};

// Sparkline data for ActivityTrendsCard (last 7 days trend)
const SPARKLINE_DATA = {
  calls: [45, 52, 48, 55, 62, 58, 72],
  sms: [22, 28, 25, 31, 35, 29, 38],
  apps: [1, 2, 1, 3, 2, 2, 3],
  approved: [0, 1, 2, 1, 1, 2, 1],
  funded: [0, 1, 0, 1, 1, 0, 1],
  revenue: [0, 35000, 0, 42000, 38000, 0, 30000]
};

const SAMPLE_ACTIVITY_RATES = {
  callsPerDay: 114, callsPerDayHist: 98,
  smsPerDay: 63, smsPerDayHist: 55,
  appsPerDay: 4.0, appsPerDayHist: 3.5,
  approvedPerDay: 2.7, approvedPerDayHist: 2.4,
  fundedPerDay: 1.3, fundedPerDayHist: 1.1,
  revenuePerDay: 48333, revenuePerDayHist: 42000
};

const SAMPLE_DIAGNOSIS = {
  positiveChanges: [
    { metric: 'Calls/day', description: 'More outreach activity', percent: '+16.3%' },
    { metric: 'Apps/day', description: 'Higher application rate', percent: '+14.3%' }
  ],
  negativeChanges: [
    { metric: 'Approved→Funded', description: 'Lower close rate on approvals', percent: '-9.1%' }
  ],
  diagnosis: "Activity is up significantly (+16% calls, +14% apps). However, Approved→Funded conversion is down 5pp, meaning approvals are not closing at the usual rate. This could indicate underwriting issues or customer hesitation.",
  actionItems: [
    "Review recent declined approvals for patterns",
    "Follow up on pending approved deals that haven't funded",
    "Check if underwriting requirements have changed recently"
  ]
};

// ============================================
// OVERVIEW TAB MOCKUP COMPONENT
// ============================================
export default function OverviewTabMockup() {
  const [selectedView, setSelectedView] = useState('Team');

  // Set unique browser tab title
  useEffect(() => {
    document.title = '🎨 MOCKUP: Overview Tab';
  }, []);

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <h1>Sales Performance Dashboard</h1>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          backgroundColor: '#fef3c7',
          padding: '8px 16px',
          borderRadius: '6px',
          border: '2px solid #f59e0b'
        }}>
          <span style={{ fontSize: '18px' }}>🎨</span>
          <span style={{ fontWeight: 600, color: '#92400e' }}>
            MOCKUP MODE - Proposed Overview Tab Enhancements | 🔧 P2 FIX: box-shadow standardized
          </span>
        </div>
      </div>

      {/* Tab Navigation (just for visual context) */}
      <nav className="tab-navigation">
        <button className="active">Overview</button>
        <button>Weekly</button>
        <button>Monthly</button>
        <button>Historical</button>
      </nav>

      {/* Main Content */}
      <main className="dashboard-content">
        <div className="overview-tab">

          {/* Welcome Banner - UNCHANGED */}
          <div style={{
            backgroundColor: '#f8fafc',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '24px',
            borderLeft: '4px solid #3b82f6'
          }}>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', color: '#1e293b' }}>
              Welcome to Your Sales Dashboard
            </h2>
            <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
              Track your month-to-date performance, see what you need to hit your goals, and get actionable insights to improve.
            </p>
          </div>

          {/* MTD Review Card - ENHANCED with icon bullets */}
          <MTDReviewCardEnhanced
            selectedView={selectedView}
            setSelectedView={setSelectedView}
            reps={SAMPLE_REPS}
          />

          {/* Funnel Story Card - ENHANCED with responsive grid */}
          <FunnelStoryCardEnhanced />

          {/* Activity Trends Card - ENHANCED with sparklines */}
          <ActivityTrendsCardEnhanced />

          {/* Diagnosis Card - UNCHANGED (already well-designed) */}
          <DiagnosisCardMockup />

        </div>
      </main>

      {/* Enhancement Legend */}
      <div style={{
        marginTop: '24px',
        padding: '20px',
        backgroundColor: '#eff6ff',
        borderRadius: '8px',
        border: '2px solid #3b82f6'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#1e40af' }}>
          Enhancements in This Mockup:
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '6px' }}>
            <strong style={{ color: '#10b981' }}>1. ActivityTrendsCard</strong>
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#64748b' }}>
              Added sparkline mini-charts showing 7-day trend for each metric
            </p>
          </div>
          <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '6px' }}>
            <strong style={{ color: '#10b981' }}>2. MTDReviewCard Guidance</strong>
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#64748b' }}>
              Added icons to guidance bullets for faster visual scanning
            </p>
          </div>
          <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '6px' }}>
            <strong style={{ color: '#10b981' }}>3. FunnelStoryCard Grid</strong>
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#64748b' }}>
              Improved responsive breakpoints for conversion rates grid
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// ENHANCED MTD REVIEW CARD
// Enhancement: Icons on guidance bullets
// ============================================
function MTDReviewCardEnhanced({ selectedView, setSelectedView, reps }) {
  const metrics = selectedView === 'Team' ? SAMPLE_METRICS.team : SAMPLE_METRICS['John Smith'];

  const getExpected = (goal) => (goal / BUSINESS_DAYS_IN_MONTH) * BUSINESS_DAYS_ELAPSED;

  const getStatus = (current, goal) => {
    const expected = getExpected(goal);
    const pace = expected > 0 ? (current / expected) * 100 : 0;
    if (pace >= 80) return { label: 'ON TRACK', icon: '✅', color: '#10b981' };
    if (pace >= 60) return { label: 'BEHIND', icon: '⚠️', color: '#f59e0b' };
    return { label: 'CRITICAL', icon: '🚨', color: '#ef4444' };
  };

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(value);

  const overallStatus = getStatus(metrics.revenue, metrics.revenueGoal);
  const displayName = selectedView === 'Team' ? 'the Team' : selectedView.split(' ')[0];

  // Calculate what's needed
  const revenueNeeded = Math.max(0, metrics.revenueGoal - metrics.revenue);
  const revenuePerDay = BUSINESS_DAYS_REMAINING > 0 ? Math.ceil(revenueNeeded / BUSINESS_DAYS_REMAINING) : 0;
  const appsNeeded = Math.max(0, metrics.appsGoal - metrics.apps);
  const appsPerDay = BUSINESS_DAYS_REMAINING > 0 ? Math.ceil(appsNeeded / BUSINESS_DAYS_REMAINING) : 0;
  const callsNeeded = Math.max(0, metrics.callsGoal - metrics.calls);
  const callsPerDay = BUSINESS_DAYS_REMAINING > 0 ? Math.ceil(callsNeeded / BUSINESS_DAYS_REMAINING) : 0;
  const fundedNeeded = Math.max(0, metrics.fundedGoal - metrics.funded);
  const fundedPerDay = BUSINESS_DAYS_REMAINING > 0 ? Math.ceil(fundedNeeded / BUSINESS_DAYS_REMAINING) : 0;

  return (
    <div className="mtd-review-card" style={{ marginBottom: '20px' }}>
      {/* Header with dropdown */}
      <div className="mtd-header">
        <h2>🎯 How Am I Doing This Month? ({CURRENT_MONTH})</h2>
        <select
          value={selectedView}
          onChange={(e) => setSelectedView(e.target.value)}
          className="view-selector"
        >
          <option value="Team">Team</option>
          {reps.map(rep => (
            <option key={rep} value={rep}>{rep.split(' ')[0]}</option>
          ))}
        </select>
      </div>

      {/* Status line */}
      <div className="status-line" style={{ color: overallStatus.color }}>
        {overallStatus.icon} Status: <strong>{overallStatus.label}</strong> | {BUSINESS_DAYS_REMAINING} business days remaining
      </div>

      {/* Metrics grid */}
      <div className="metrics-grid">
        <MetricCard
          label="Revenue"
          value={formatCurrency(metrics.revenue)}
          expected={formatCurrency(getExpected(metrics.revenueGoal))}
          goal={formatCurrency(metrics.revenueGoal)}
          status={getStatus(metrics.revenue, metrics.revenueGoal)}
          diff={metrics.revenue - getExpected(metrics.revenueGoal)}
          formatDiff={formatCurrency}
          businessDay={BUSINESS_DAYS_ELAPSED}
        />
        <MetricCard
          label="Apps In"
          value={metrics.apps}
          expected={Math.round(getExpected(metrics.appsGoal))}
          goal={metrics.appsGoal}
          status={getStatus(metrics.apps, metrics.appsGoal)}
          diff={metrics.apps - Math.round(getExpected(metrics.appsGoal))}
          businessDay={BUSINESS_DAYS_ELAPSED}
        />
        <MetricCard
          label="Calls"
          value={metrics.calls}
          expected={Math.round(getExpected(metrics.callsGoal))}
          goal={metrics.callsGoal}
          status={getStatus(metrics.calls, metrics.callsGoal)}
          diff={metrics.calls - Math.round(getExpected(metrics.callsGoal))}
          businessDay={BUSINESS_DAYS_ELAPSED}
        />
        <MetricCard
          label="Funded"
          value={metrics.funded}
          expected={Math.round(getExpected(metrics.fundedGoal))}
          goal={metrics.fundedGoal}
          status={getStatus(metrics.funded, metrics.fundedGoal)}
          diff={metrics.funded - Math.round(getExpected(metrics.fundedGoal))}
          businessDay={BUSINESS_DAYS_ELAPSED}
        />
        <MetricCard
          label="Approved"
          value={metrics.approved}
          expected={Math.round(getExpected(metrics.approvedGoal))}
          goal={metrics.approvedGoal}
          status={getStatus(metrics.approved, metrics.approvedGoal)}
          diff={metrics.approved - Math.round(getExpected(metrics.approvedGoal))}
          businessDay={BUSINESS_DAYS_ELAPSED}
        />
      </div>

      {/* ENHANCED: Guidance section with icons */}
      <div className="guidance-section">
        <h3>📊 What {displayName} Need{selectedView === 'Team' ? 's' : ''} to Hit Goals:</h3>

        {/* ENHANCED: Added icons to each guidance item */}
        {revenueNeeded > 0 && (
          <div className="guidance-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', marginTop: '2px' }}>💰</span>
            <div>
              <strong>Revenue:</strong> Need {formatCurrency(revenueNeeded)} more → {formatCurrency(revenuePerDay)}/day for next {BUSINESS_DAYS_REMAINING} business days
              {selectedView === 'Team' && ` (${formatCurrency(Math.ceil(revenuePerDay / reps.length))} per rep/day)`}
            </div>
          </div>
        )}

        {appsNeeded > 0 && (
          <div className="guidance-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', marginTop: '2px' }}>📋</span>
            <div>
              <strong>Apps:</strong> Need {appsNeeded} more → {appsPerDay}/day for next {BUSINESS_DAYS_REMAINING} business days
              {selectedView === 'Team' && ` (${Math.ceil(appsPerDay / reps.length)} per rep/day)`}
            </div>
          </div>
        )}

        {callsNeeded > 0 && (
          <div className="guidance-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', marginTop: '2px' }}>📞</span>
            <div>
              <strong>Calls:</strong> Need {callsNeeded} more → {callsPerDay}/day for next {BUSINESS_DAYS_REMAINING} business days
              {selectedView === 'Team' && ` (${Math.ceil(callsPerDay / reps.length)} per rep/day)`}
            </div>
          </div>
        )}

        {fundedNeeded > 0 && (
          <div className="guidance-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', marginTop: '2px' }}>✅</span>
            <div>
              <strong>Funded:</strong> Need {fundedNeeded} more deal{fundedNeeded === 1 ? '' : 's'} → {fundedPerDay}/day for next {BUSINESS_DAYS_REMAINING} business days
              {selectedView === 'Team' && ` (${Math.ceil(fundedPerDay / reps.length)} per rep/day)`}
            </div>
          </div>
        )}

        {/* Enhancement indicator */}
        <div style={{
          marginTop: '16px',
          padding: '8px 12px',
          backgroundColor: '#d1fae5',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#065f46',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>✨</span>
          <strong>ENHANCED:</strong> Added icons to each guidance item for faster visual scanning
        </div>
      </div>
    </div>
  );
}

// Metric Card Sub-component
function MetricCard({ label, value, expected, goal, status, diff, formatDiff, businessDay }) {
  const displayDiff = formatDiff ? formatDiff(Math.abs(diff)) : Math.abs(diff);

  return (
    <div className="metric-card">
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
        Expected: {expected} (by day {businessDay})
      </div>
      <div className="metric-goal">Goal: {goal}</div>
      <div className="metric-progress" style={{ color: status.color }}>
        {status.icon} {diff >= 0 ? 'Ahead' : 'Behind'} {displayDiff}
      </div>
    </div>
  );
}

// ============================================
// ENHANCED FUNNEL STORY CARD
// Enhancement: Improved responsive grid
// ============================================
function FunnelStoryCardEnhanced() {
  const { calls, sms, apps, approved, funded, revenue, conversions } = SAMPLE_FUNNEL_DATA;

  const getVariance = (current, historical) => {
    const diff = current - historical;
    let indicator = '→', color = '#64748b';
    if (diff > 0.5) { indicator = '↗'; color = '#10b981'; }
    else if (diff < -0.5) { indicator = '↘'; color = '#ef4444'; }
    return { diff: Math.round(diff * 10) / 10, indicator, color };
  };

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <span style={{ fontSize: '20px', marginRight: '8px' }}>🔄</span>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
          The Funnel Story
        </h3>
        <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          Current vs 3-Month Avg
        </span>
      </div>

      {/* Funnel Visual */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        marginBottom: '24px',
        padding: '16px 0',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <FunnelStage label="CALLS" value={calls} color="#3b82f6" />
        <FunnelArrow />
        <FunnelStage label="APPS" value={apps} color="#10b981" />
        <FunnelArrow />
        <FunnelStage label="APPROVED" value={approved} color="#8b5cf6" />
        <FunnelArrow />
        <FunnelStage label="FUNDED" value={funded} color="#f59e0b" />
      </div>

      {/* ENHANCED: Conversion Rates with better responsive grid */}
      <div style={{
        backgroundColor: '#f8fafc',
        padding: '16px',
        borderRadius: '6px',
        marginBottom: '16px'
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: 600,
          color: '#475569',
          marginBottom: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Conversion Rates (Current → 3-Mo Avg)
        </div>

        {/* ENHANCED: Better responsive grid with proper breakpoints */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px'
        }}>
          <ConversionRate
            label="Calls → Apps"
            current={conversions.callsToApps}
            historical={conversions.callsToAppsHist}
            variance={getVariance(conversions.callsToApps, conversions.callsToAppsHist)}
          />
          <ConversionRate
            label="SMS → Apps"
            current={conversions.smsToApps}
            historical={conversions.smsToAppsHist}
            variance={getVariance(conversions.smsToApps, conversions.smsToAppsHist)}
          />
          <ConversionRate
            label="Apps → Approved"
            current={conversions.appsToApproved}
            historical={conversions.appsToApprovedHist}
            variance={getVariance(conversions.appsToApproved, conversions.appsToApprovedHist)}
          />
          <ConversionRate
            label="Approved → Funded"
            current={conversions.approvedToFunded}
            historical={conversions.approvedToFundedHist}
            variance={getVariance(conversions.approvedToFunded, conversions.approvedToFundedHist)}
          />
        </div>

        {/* Enhancement indicator */}
        <div style={{
          marginTop: '16px',
          padding: '8px 12px',
          backgroundColor: '#d1fae5',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#065f46',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>✨</span>
          <strong>ENHANCED:</strong> Grid now uses <code>minmax(200px, 1fr)</code> for better mobile stacking
        </div>
      </div>

      {/* Revenue Summary */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: '#f0fdf4',
        border: '1px solid #86efac',
        borderRadius: '6px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ flex: 1, minWidth: '120px' }}>
          <div style={{ fontSize: '13px', color: '#166534', fontWeight: 500 }}>Total Revenue</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#15803d' }}>
            ${(revenue / 1000).toFixed(1)}K
          </div>
        </div>
        <div style={{ flex: 1, minWidth: '100px', textAlign: 'center' }}>
          <div style={{ fontSize: '13px', color: '#166534', fontWeight: 500 }}>Deals Closed</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#15803d' }}>{funded}</div>
        </div>
        <div style={{ flex: 1, minWidth: '120px', textAlign: 'right' }}>
          <div style={{ fontSize: '13px', color: '#166534', fontWeight: 500 }}>Avg Deal Size</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#15803d' }}>
            ${(revenue / funded / 1000).toFixed(1)}K
          </div>
        </div>
      </div>
    </div>
  );
}

function FunnelStage({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', minWidth: '70px' }}>
      <div style={{
        backgroundColor: color,
        color: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        fontWeight: 700,
        fontSize: '22px',
        marginBottom: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        {value}
      </div>
      <div style={{
        fontSize: '11px',
        fontWeight: 600,
        color: '#475569',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        {label}
      </div>
    </div>
  );
}

function FunnelArrow() {
  return (
    <div style={{ fontSize: '24px', color: '#cbd5e1', margin: '0 4px' }}>→</div>
  );
}

function ConversionRate({ label, current, historical, variance }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 12px',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '13px', fontWeight: 500, color: '#475569' }}>{label}:</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>
          {current.toFixed(1)}%
        </span>
        <span style={{ fontSize: '14px', fontWeight: 600, color: variance.color }}>
          {variance.indicator}
        </span>
        <span style={{ fontSize: '12px', color: '#64748b' }}>
          (vs {historical.toFixed(1)}%)
        </span>
      </div>
    </div>
  );
}

// ============================================
// ENHANCED ACTIVITY TRENDS CARD
// Enhancement: Added sparkline mini-charts
// ============================================
function ActivityTrendsCardEnhanced() {
  const rates = SAMPLE_ACTIVITY_RATES;

  const getColor = (label, variance) => {
    if (['Revenue/day', 'Apps/day', 'Funded/day', 'Approved/day'].includes(label)) {
      if (variance > 5) return '#10b981';
      if (variance < -5) return '#ef4444';
    } else {
      if (variance > 5) return '#3b82f6';
      if (variance < -5) return '#f59e0b';
    }
    return '#64748b';
  };

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <span style={{ fontSize: '20px', marginRight: '8px' }}>📈</span>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
          Activity Trends
        </h3>
        <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          Per Business Day: Current vs 3-Month Avg
        </span>
      </div>

      {/* ENHANCED: Activity Metrics Grid with Sparklines */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <ActivityMetricWithSparkline
          label="Calls/day"
          value={rates.callsPerDay}
          historical={rates.callsPerDayHist}
          sparklineData={SPARKLINE_DATA.calls}
          format="number"
          color={getColor('Calls/day', ((rates.callsPerDay - rates.callsPerDayHist) / rates.callsPerDayHist) * 100)}
        />
        <ActivityMetricWithSparkline
          label="SMS/day"
          value={rates.smsPerDay}
          historical={rates.smsPerDayHist}
          sparklineData={SPARKLINE_DATA.sms}
          format="number"
          color={getColor('SMS/day', ((rates.smsPerDay - rates.smsPerDayHist) / rates.smsPerDayHist) * 100)}
        />
        <ActivityMetricWithSparkline
          label="Apps/day"
          value={rates.appsPerDay}
          historical={rates.appsPerDayHist}
          sparklineData={SPARKLINE_DATA.apps}
          format="decimal"
          color={getColor('Apps/day', ((rates.appsPerDay - rates.appsPerDayHist) / rates.appsPerDayHist) * 100)}
        />
        <ActivityMetricWithSparkline
          label="Approved/day"
          value={rates.approvedPerDay}
          historical={rates.approvedPerDayHist}
          sparklineData={SPARKLINE_DATA.approved}
          format="decimal"
          color={getColor('Approved/day', ((rates.approvedPerDay - rates.approvedPerDayHist) / rates.approvedPerDayHist) * 100)}
        />
        <ActivityMetricWithSparkline
          label="Funded/day"
          value={rates.fundedPerDay}
          historical={rates.fundedPerDayHist}
          sparklineData={SPARKLINE_DATA.funded}
          format="decimal"
          color={getColor('Funded/day', ((rates.fundedPerDay - rates.fundedPerDayHist) / rates.fundedPerDayHist) * 100)}
        />
        <ActivityMetricWithSparkline
          label="Revenue/day"
          value={rates.revenuePerDay}
          historical={rates.revenuePerDayHist}
          sparklineData={SPARKLINE_DATA.revenue}
          format="currency"
          color={getColor('Revenue/day', ((rates.revenuePerDay - rates.revenuePerDayHist) / rates.revenuePerDayHist) * 100)}
        />
      </div>

      {/* Enhancement indicator */}
      <div style={{
        padding: '12px',
        backgroundColor: '#d1fae5',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#065f46',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span>✨</span>
        <strong>ENHANCED:</strong> Added sparkline mini-charts showing 7-day trend for each metric
      </div>

      {/* Baseline Info */}
      <div style={{
        fontSize: '12px',
        color: '#64748b',
        textAlign: 'center',
        fontStyle: 'italic',
        padding: '8px',
        backgroundColor: '#f8fafc',
        borderRadius: '4px',
        marginTop: '12px'
      }}>
        Baseline: 3-month rolling average (Sept + Oct + Nov)
      </div>
    </div>
  );
}

// ENHANCED: Activity Metric with Sparkline
function ActivityMetricWithSparkline({ label, value, historical, sparklineData, format, color }) {
  const variance = value - historical;
  const variancePercent = historical !== 0 ? (variance / historical) * 100 : 0;

  let indicator = '→';
  if (variancePercent > 5) indicator = '↗';
  else if (variancePercent < -5) indicator = '↘';

  const formatValue = (val) => {
    if (format === 'currency') return `$${(val / 1000).toFixed(1)}K`;
    if (format === 'decimal') return val.toFixed(1);
    return Math.round(val);
  };

  // Convert sparkline data for the chart
  const chartData = sparklineData.map((val, idx) => ({ day: idx + 1, value: val }));

  return (
    <div style={{
      padding: '12px',
      backgroundColor: '#f8fafc',
      borderRadius: '6px',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          {/* Label */}
          <div style={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#475569',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {label}
          </div>

          {/* Current Value */}
          <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: '6px' }}>
            <span style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b', marginRight: '8px' }}>
              {formatValue(value)}
            </span>
            <span style={{ fontSize: '20px', color, fontWeight: 600 }}>
              {indicator}
            </span>
          </div>

          {/* Variance */}
          <div style={{ fontSize: '13px', color: '#64748b' }}>
            <span style={{ fontWeight: 600, color }}>
              {variance > 0 ? '+' : ''}{formatValue(variance)}
            </span>
            {' '}vs{' '}
            <span style={{ fontWeight: 500 }}>{formatValue(historical)}/day avg</span>
          </div>

          {/* Percentage Change */}
          <div style={{ fontSize: '12px', fontWeight: 600, color, marginTop: '4px' }}>
            {variancePercent > 0 ? '+' : ''}{variancePercent.toFixed(1)}%
          </div>
        </div>

        {/* ENHANCED: Sparkline Chart */}
        <div style={{ width: '80px', height: '40px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ============================================
// DIAGNOSIS CARD (UNCHANGED - Already good)
// ============================================
function DiagnosisCardMockup() {
  const { positiveChanges, negativeChanges, diagnosis, actionItems } = SAMPLE_DIAGNOSIS;

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '20px', marginRight: '8px' }}>💡</span>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
              What's Different & Why
            </h3>
          </div>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', marginLeft: '28px' }}>
            Compared to your 3-month average • Automated insights
          </div>
        </div>
      </div>

      {/* Positive Changes */}
      {positiveChanges.length > 0 && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: '6px'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#166534',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ marginRight: '6px' }}>✅</span>
            POSITIVE CHANGES:
          </div>
          <ul style={{ margin: 0, paddingLeft: '20px', listStyleType: 'disc' }}>
            {positiveChanges.map((change, idx) => (
              <li key={idx} style={{ fontSize: '13px', color: '#15803d', marginBottom: '4px', lineHeight: '1.5' }}>
                <strong>{change.metric}</strong> {change.description} ({change.percent})
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Negative Changes */}
      {negativeChanges.length > 0 && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: '6px'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#991b1b',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ marginRight: '6px' }}>🔴</span>
            AREAS OF CONCERN:
          </div>
          <ul style={{ margin: 0, paddingLeft: '20px', listStyleType: 'disc' }}>
            {negativeChanges.map((change, idx) => (
              <li key={idx} style={{ fontSize: '13px', color: '#b91c1c', marginBottom: '4px', lineHeight: '1.5' }}>
                <strong>{change.metric}</strong> {change.description} ({change.percent})
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Diagnosis */}
      <div style={{
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: '#f8fafc',
        border: '1px solid #cbd5e1',
        borderRadius: '6px'
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: 700,
          color: '#475569',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <span style={{ marginRight: '6px' }}>📊</span>
          DIAGNOSIS:
        </div>
        <div style={{ fontSize: '13px', color: '#1e293b', lineHeight: '1.6' }}>
          {diagnosis}
        </div>
      </div>

      {/* Action Items */}
      {actionItems.length > 0 && (
        <div style={{
          padding: '12px',
          backgroundColor: '#eff6ff',
          border: '1px solid #93c5fd',
          borderRadius: '6px'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#1e40af',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ marginRight: '6px' }}>🎯</span>
            ACTION ITEMS:
          </div>
          <ol style={{ margin: 0, paddingLeft: '20px', listStyleType: 'decimal' }}>
            {actionItems.map((action, idx) => (
              <li key={idx} style={{ fontSize: '13px', color: '#1e40af', marginBottom: '4px', lineHeight: '1.5' }}>
                {action}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
